




























import { Vue, Component, InjectReactive, Prop } from 'vue-property-decorator';
import { inject } from 'inversify-props';
import ConversationUserPermissionsModel from '@/models/crm/conversation-user-permissions.model';
import { InjectionIdEnum } from '@/enums/injection-id.enum';
import ConversationService from '@/services/crm/conversation.service';
import { ConversationContactStatusEnum } from '@/enums/crm/conversation-contact-status.enum';
import ContactModel from '@/models/crm/contact.model';
import { ICreateConversation } from '@/interfaces/crm/create-conversation.interface';
import SettingsService from '@/services/crm/settings.service';
import SettingsModel from '@/models/crm/settings.model';
import SessionService from '@/services/session.service';
import UserContactInfo from '@/models/crm/user-contact-info.model';
import { EvoTalksPhoneHelper } from '@/utils/helpers/evotalks-phone-helper';
import EvoTalksSyncingDialog from '@/components/crm/evotalks-syncing-dialog.vue';
import CRMEvoTalksService from '@/services/crm/evotalks.service';

@Component({
  components: {
    EvoTalksSyncingDialog,
  },
})
export default class CrmChatCallerButton extends Vue {
  @inject(InjectionIdEnum.CrmConversationService)
  private conversationService!: ConversationService;

  @inject(InjectionIdEnum.CRMEvoTalksService)
  private evotalksService!: CRMEvoTalksService;

  @inject(InjectionIdEnum.CrmSettingsService)
  private settingsService!: SettingsService;

  @inject(InjectionIdEnum.SessionService)
  private sessionService!: SessionService;

  @InjectReactive('conversationUserPermission') readonly conversationUserPermission!: ConversationUserPermissionsModel;

  @InjectReactive('userContactInfo') readonly userContactInfo!: UserContactInfo;

  @Prop({ required: true })
  contactNumber!: string;

  @Prop()
  clientId!: string;

  @Prop()
  contact!: ContactModel;

  @Prop()
  width!: boolean | string;

  @Prop()
  height!: boolean | string;

  @Prop()
  color!: string;

  @Prop({ default: false })
  disabled!: boolean;

  @Prop()
  nameButton!: string;

  @Prop({ default: true })
  fab!: boolean;

  @Prop({ default: 'crm.chatCallerButton.messages.InactiveContact' })
  tooltipDisabled!: string;

  /**
   * Contrato de formatacao do numero copiado: 'contact' = whatsapp do contato (canonico de
   * WhatsApp, ganha o nono digito); 'company' = telefone do cliente/prospect, que o sync de
   * empresa grava no EvoTalks SEM o nono digito.
   */
  @Prop({ default: 'contact' })
  phoneSource!: 'contact' | 'company';

  /**
   * 'auto' mantem o comportamento historico (EvoTalks quando configurado, senao GeoChat).
   * 'evotalks'/'geochat' fixam o destino — usado quando a tela mostra os dois botoes lado a lado.
   */
  @Prop({ default: 'auto' })
  target!: 'auto' | 'evotalks' | 'geochat';

  loading = false;

  syncingEvoTalks = false;

  private settings!: SettingsModel;

  async mounted(): Promise<void> {
    this.settings = await this.settingsService.getSettings();
  }

  async onClick(): Promise<void> {
    this.loading = true;
    try {
      const evoTalksConfigured = !!(this.settings.flagUtilizaEvoTalks && this.settings.urlBaseEvoTalks);
      if (this.target === 'evotalks' && !evoTalksConfigured) {
        return;
      }
      if (this.target !== 'geochat' && evoTalksConfigured) {
        // Tudo que pode lancar fica ANTES de abrir a janela: janela aberta e abandonada por erro
        // deixaria uma aba about:blank orfa, e o catch do metodo nao alcanca a variavel de bloco.
        const normalizedNumber = this.normalizeContactNumber();
        const clipboardNumber = this.getClipboardContactNumber();
        const evoTalksUrl = new URL('/base/agentchat', this.settings.urlBaseEvoTalks);
        evoTalksUrl.searchParams.set('n', normalizedNumber);
        // A copia e DISPARADA aqui, antes do window.open: a Clipboard API exige
        // document.hasFocus() e abrir a janela tira o foco do documento. O resultado e lido
        // depois — copiar e conveniencia e nunca pode impedir a conversa de abrir.
        const copia = CrmChatCallerButton.copiarParaAreaDeTransferencia(clipboardNumber);
        // A janela e aberta AGORA, ainda dentro do gesto do clique: depois do await do sync o
        // navegador ja nao associa o window.open ao gesto e bloqueia o popup em silencio.
        const janela = window.open('', 'janelaCompartilhada');
        await this.garantirSincronizacaoEvoTalks();
        // Notificar a copia ANTES de trocar de aba: em falha esta mensagem traz o numero para
        // colar na mao, e na aba que perdeu o foco o usuario nao a veria.
        if (await copia) {
          this.$notify.success(this.$t('crm.chatCallerButton.messages.numberCopiedToClipboard'));
        } else {
          this.$notify.warn(
            this.$t('crm.chatCallerButton.messages.numberCopyFailed', { contactNumber: clipboardNumber }),
            8000,
          );
        }
        // A janela pode ter sido fechada pelo usuario durante o sync: janela.closed torna
        // location.href/focus() um no-op silencioso, entao vale reabrir (e avisar se bloqueada).
        let alvo = janela && !janela.closed ? janela : null;
        if (alvo) {
          alvo.location.href = evoTalksUrl.toString();
        } else {
          alvo = window.open(evoTalksUrl.toString(), 'janelaCompartilhada');
        }
        if (alvo) {
          alvo.focus();
        } else {
          this.$notify.warn(this.$t('crm.chatCallerButton.messages.popupBlocked'), 8000);
        }
        this.loading = false;
        return;
      }

      if (this.canOpenConversation()) {
        const normalizedNumber = this.normalizeContactNumber();
        const checkResult = await this.conversationService.checkContactNumber(normalizedNumber);
        const validWaContact = checkResult.find((x) => x.status === ConversationContactStatusEnum.Valid);

        if (validWaContact) {
          const payload: ICreateConversation = {
            contact: this.contact,
            waContact: validWaContact,
          };
          const { apiToken, apiUrl, socketIOUrl } = this.sessionService;
          this.$store.commit('setContactNumber', normalizedNumber);
          this.$store.commit('setClientId', this.clientId);
          this.$store.commit('setCreateConversationPayload', payload);
          this.$store.commit('setUserContactInfo', this.userContactInfo);

          const chatWindowName = 'GeoChatWindow';
          const chatWindowUrl = this.$router.resolve({
            name: 'GeoChatView',
            query: {
              token: apiToken,
              apiUrl,
              socketIOUrl,
            },
          }).href;

          let chatWindow = window.open('', chatWindowName);

          if (!chatWindow || chatWindow.closed) {
            chatWindow = window.open(chatWindowUrl, chatWindowName);
          } else {
            chatWindow.location.href = chatWindowUrl;
            chatWindow.focus();
          }
          this.loading = false;
        } else {
          this.$notify.warn(
            this.$t('crm.chatCallerButton.messages.couldNotFoundAValidWaIdForNumber', {
              contactNumber: normalizedNumber,
            }),
            5000,
          );
        }
      }
    } catch (err) {
      this.$notify.error(err && (err as Error).message);
    } finally {
      this.loading = false;
    }
  }

  get showButton(): boolean {
    return !!this.conversationUserPermission;
  }

  get filterTooltipMessage(): string {
    return `${this.$t(this.tooltipDisabled)}`;
  }

  private normalizeContactNumber():string {
    const onlyNumbers = this.contactNumber.replace(/[^0-9]/g, '');

    const numbersWith55 = (onlyNumbers.length === 10 || onlyNumbers.length === 11)
      ? `55${onlyNumbers}`
      : onlyNumbers;

    if (numbersWith55.length === 13) {
      return numbersWith55.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, '+$1 ($2) $3-$4');
    }

    if (numbersWith55.length === 12) {
      return numbersWith55.replace(/(\d{2})(\d{2})(\d{4})(\d{4})/, '+$1 ($2) $3-$4');
    }

    return numbersWith55;
  }

  private async garantirSincronizacaoEvoTalks(): Promise<void> {
    // Contato sintetico do prospect (id=-1, sem registro): sincroniza a EMPRESA on-demand
    if (this.contact && (!this.contact.id || this.contact.id <= 0)) {
      await this.garantirSincronizacaoProspect();
      return;
    }
    if (!this.contact || !this.contact.id) {
      return;
    }
    // So pula a sincronizacao quando o status CONFIRMA que contato + empresa ja estao
    // sincronizados. Se a leitura do status falhar, tenta sincronizar mesmo assim
    // (best-effort) — nao pular silenciosamente.
    try {
      const status = await this.conversationService.getEvoTalksSyncStatus(this.contact.id);
      if (!status.evoTalksAtivo || (status.contactSynced && status.companySynced)) {
        return;
      }
    } catch (err) {
      // status indisponivel: segue para a tentativa de sincronizacao
    }
    this.syncingEvoTalks = true;
    try {
      const result = await this.conversationService.ensureEvoTalksSync(this.contact.id);
      if (!result.contactSynced || !result.companySynced) {
        this.$notify.warn(this.$t('crm.chatCallerButton.messages.evoTalksSyncIncomplete'), 5000);
      }
    } catch (err) {
      // sync on-demand nao pode impedir a conversa: segue para o EvoTalks mesmo em falha
      this.$notify.warn(this.$t('crm.chatCallerButton.messages.evoTalksSyncIncomplete'), 5000);
    } finally {
      this.syncingEvoTalks = false;
    }
  }

  private async garantirSincronizacaoProspect(): Promise<void> {
    const prospectId = this.contact?.idProspect;
    if (!prospectId) {
      return;
    }
    // So pula quando o status CONFIRMA empresa sincronizada; falha na leitura -> tenta sincronizar
    try {
      const status = await this.evotalksService.getStatus(null, prospectId);
      if (status.empresaSincronizada) {
        return;
      }
    } catch (err) {
      // status indisponivel: segue para a tentativa de sincronizacao
    }
    this.syncingEvoTalks = true;
    try {
      const result = await this.evotalksService.syncEmpresa(null, prospectId);
      if (result.synced) {
        await this.evotalksService.syncContatos(null, prospectId);
      } else {
        this.$notify.warn(this.$t('crm.chatCallerButton.messages.evoTalksSyncIncomplete'), 5000);
      }
    } catch (err) {
      this.$notify.warn(this.$t('crm.chatCallerButton.messages.evoTalksSyncIncomplete'), 5000);
    } finally {
      this.syncingEvoTalks = false;
    }
  }

  private getClipboardContactNumber(): string {
    // Mesma forma em que o numero esta gravado no EvoTalks ("55 47 99988-7766"),
    // para o usuario colar direto na busca de la
    return this.phoneSource === 'company'
      ? EvoTalksPhoneHelper.formatoEvoTalksEmpresa(this.contactNumber)
      : EvoTalksPhoneHelper.formatoEvoTalks(this.contactNumber);
  }

  /**
   * A copia e conveniencia: a Clipboard API exige document.hasFocus() (e some em origem
   * http), entao a falha e esperada e vira `false` em vez de derrubar o fluxo.
   */
  private static copiarParaAreaDeTransferencia(numero: string): Promise<boolean> {
    try {
      const clipboard = navigator.clipboard as Clipboard | undefined;
      if (!clipboard) {
        return Promise.resolve(false);
      }
      return clipboard.writeText(numero).then(() => true, () => false);
    } catch {
      return Promise.resolve(false);
    }
  }

  private canOpenConversation(): boolean {
    if (!this.conversationUserPermission) {
      this.$notify.error(this.$t('crm.chatCallerButton.messages.errorOnGetPermissions'), 5000);
      return false;
    }

    if (!this.conversationUserPermission.viewWhatsApp) {
      this.$notify.warn(this.$t('crm.chatCallerButton.messages.whatsappDisabled'), 5000);
      return false;
    }

    if (!this.conversationUserPermission.manageWhatsApp) {
      this.$notify.warn(this.$t('crm.chatCallerButton.messages.youDoNotHavePermissiontoManageWhatsApp'), 5000);
      return false;
    }

    return true;
  }
}
